<template>
    <div>

        <vue-headful title="Inspections, Observations, Corrective Action Totals :: MeritEHS"/>

        <div :class="pdf_view ? 'container' : ''" :style="pdf_view ? 'max-width: 980px;' : ''">

            <div class="row d-none d-print-block" v-if="pdfLogo" style="margin-bottom: -20px; z-index: 100;">
                <div class="col-md-3">
                    <img :src="pdfLogo" class="img-fluid" />
                </div>
            </div>

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0">
                    <li class="breadcrumb-item active">Inspections, Observations, Corrective Action Totals <template v-if="type_label">- {{ type_label }}</template></li>
                </ol>
            </h4>

            <div class="alert alert-info d-print-none" v-if="pdf_view">
                <strong>How to Export as PDF:</strong>
                <ol>
                    <li>On your browser, there should be a "File" or "Options" tab with a "Print" option. You may also hold down the "Ctrl" key and press the "P" key.</li>
                    <li>This will bring up the print page options box. There should be an option called "Select Printer" or "Destination".</li>
                    <li>Select "Save as PDF" or "Adobe PDF".</li>
                    <li>Click "Save" or "Print". This will open your file explorer and let you save it to your computer as a PDF.</li>
                </ol>
            </div>

            <div class="card mb-4 d-print-none">
                <div class="card-body">
                    <div class="d-inline-block">
                        <b-form-select v-model="filters.report_type" :options="reportTypeOptions" :value="null" />
                    </div>

                    <div class="d-inline-block" v-if="filters.report_type === 'Jobsite'" style="width: 250px;">
                        <jobsite-select :forFilters="true" v-on:vselected="setReportTypeValue" />
                    </div>

                    <div class="d-inline-block" v-if="filters.report_type === 'User'" style="width: 250px;">
                        <user-select :forFilters="true" v-on:vuselected="setReportTypeValue" placeholder="Users" />
                    </div>

                    <div class="d-inline-block" v-if="filters.report_type === 'Employee'" style="width: 250px;">
                        <employee-select :forFilters="true" v-on:vselected="setReportTypeValue" placeholder="Employees" />
                    </div>

                    <div class="d-inline-block" v-if="filters.report_type === 'Division'">
                        <multi-select :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="divisions" @selectionChanged="setReportTypeValue"></multi-select>
                    </div>

                    <div class="d-inline-block">
                        <date-range-selector ref="inspDate" buttonTitle="Date" v-model="filters.date_range" :value="filters.date_range"/>
                    </div>
                    <div class="d-inline-block">
                        <multi-select v-model="filters.inspection_forms" :btnLabel="(values) => ('Inspection Forms ('+values.length+')')" :selectOptions="inspection_forms" :options="{multi: true}">
                            <option v-for="form in inspection_forms" v-bind:value="form.inspection_form_id">{{ form.form_name }}</option>
                        </multi-select>
                    </div>

                    <div class="my-2">
                        <b-form-checkbox v-model="pdf_view" :value="true" :unchecked-value="false" plain>PDF Friendly View</b-form-checkbox>
                    </div>

                    <div class="mt-3">
                        <button type="button" class="btn btn-sm btn-warning" v-on:click="clearFilters()">
                            <i class="fa fa-times"></i> Clear
                        </button>
                        <button type="button" class="btn btn-sm btn-success" v-on:click="applyFilters()" :disabled="loading || reportTypeEmpty">
                            <template v-if="reportTypeEmpty">
                                Select a Report Type and Value to Build Charts
                            </template>
                            <template>
                                <i class="fa fa-filter"></i> Apply <i class="fa fa-spin fa-spinner" v-show="loading"></i>
                            </template>
                        </button>
                    </div>
                </div>
            </div>

            <div class="card mb-4 d-print-none">
                <div class="card-body">
                    <div class="form-inline">
                        <div class="form-group mr-2">
                            <label>Compliant Label:</label>
                            <b-form-input v-model="compliant_label" size="sm" placeholder="Enter a label for Compliant" />
                        </div>
                        <div class="form-group">
                            <label>Non-Compliant Label:</label>
                            <b-form-input v-model="non_compliant_label" size="sm" placeholder="Enter a label for Non-Compliant" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mb-4">
                <div class="col-md-6 col-12">
                    <div class="card" style="min-height: 250px;">
                        <div class="card-header">Totals</div>
                        <div class="card-body">
                            <div class="row mb-2">
                                <div class="col-md-7 text-md-right text-muted">Inspections:</div>
                                <div class="col-md-5">{{ total_insps }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-7 text-md-right text-muted">Observations:</div>
                                <div class="col-md-5">{{ total_obsvs }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-7 text-md-right text-muted">Corrective Actions:</div>
                                <div class="col-md-5">{{ total_actions }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-7 text-md-right text-muted">CA Time to Completion:</div>
                                <div class="col-md-5">{{ avg_ttc }}</div>
                            </div>
                            <template v-if="filters.report_type === 'User'">
                                <div class="row mb-2">
                                    <div class="col-md-7 text-md-right text-muted">Actions Created:</div>
                                    <div class="col-md-5">{{ user_actions_created }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-7 text-md-right text-muted">Actions Assigned:</div>
                                    <div class="col-md-5">{{ user_actions_assigned }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-7 text-md-right text-muted">Actions Completed:</div>
                                    <div class="col-md-5">{{ user_actions_completed }}</div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-md-6 col-12">
                    <div class="card" style="min-height: 250px;">
                        <div class="card-body">
                            <div v-if="loading">
                                <i class="fa fa-spin fa-spinner"></i> Initializing
                            </div>
                            <div v-else>
                                <highcharts :options="pieChartOptions" style="height: 200px;" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-body">
                            <div class="d-print-none mb-4">
                                <div class="d-inline-block">
                                    <multi-select v-model="filters.interval" :btnLabel="(values) => ('X-Axis Interval' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="intervalOptions" :options="{multi: false}"></multi-select>
                                </div>
                            </div>
                            <div v-if="loading">
                                <i class="fa fa-spin fa-spinner"></i> Initializing
                            </div>
                            <div v-else>
                                <highcharts :options="lineChartOptions" style="height: 600px;"/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
    import Highcharts from 'highcharts';
    export default {
        data: function() {
            return {
                pdf_view: (this.$route.query.pdf === 'true'),
                pdfLogo: this.$store.state.user.pdf_logo,
                compliantNoncompliantSeries: [],
                line_chart_series: [],
                labels: [],
                jobsites: [],
                loading: false,
                show_avg_filters: true,
                filterCount: 0,
                inspection_forms: [],
                divisions: [],
                totals: [],
                filters: {
                    report_type: null,
                    date_range: 'ytd',
                    interval: [{'name': 'Monthly', 'id': 'Month'}],
                    UserId: null,
                    EmployeeId: null,
                    DivisionId: null,
                    JobsiteId: null,
                    inspection_forms: [],
                },

                intervalOptions: [
                    {id: 'Week', name: 'Weekly'},
                    {id: 'Month', name: 'Monthly'},
                    {id: 'Year', name: 'Yearly'}
                ],

                graphByOptions: [
                    {id: 'JobsiteId', name: 'Jobsite'},
                    {id: 'inspection_form_id', name: 'Inspection Form'},
                ],

                type_label: null,
                total_insps: null,
                total_obsvs: null,
                total_actions: null,
                total_compliant: null,
                total_noncompliant: null,
                avg_ttc: null,

                //labels
                compliant_label: 'Compliant',
                non_compliant_label: 'Non-Compliant',
            }
        },

        computed: {
            compliantLabel(){
                //Check input
                if(this.compliant_label)
                {
                    return this.compliant_label;
                }

                return 'Compliant';
            },

            nonCompliantLabel(){
                //Check input
                if(this.non_compliant_label)
                {
                    return this.non_compliant_label;
                }

                return 'Non-Compliant';
            },

            reportTypeEmpty(){
                //return this.filters.report_type == null || (this.filters.JobsiteId === null && this.filters.UserId === null && this.filters.DivisionId === null);
                return false;
            },

            pieChartOptions(){
                //Setup
                var vm = this;

                return {
                    chart: {
                        type: 'pie',
                    },
                    title: {
                        text: this.compliantLabel+' vs. '+this.nonCompliantLabel,
                    },
                    subtitle: {
                        text: this.nonCompliantLabel+' results include Observations'
                    },
                    plotOptions: {
                        pie: {
                            allowPointSelect: true,
                            cursor: 'pointer',
                            dataLabels: {
                                enabled: true,
                                format: '<b>{point.name}</b>: {point.percentage:.1f} %'
                            }
                        }
                    },
                    series: [{
                        name: 'Results',
                        colorByPoint: true,
                        data: [{
                            name: this.compliantLabel,
                            y: this.total_compliant,
                            // sliced: true,
                            // selected: true
                        }, {
                            name: this.nonCompliantLabel,
                            y: this.total_noncompliant
                        }]
                    }]
                }
            },

            lineChartOptions(){
                //Setup
                var vm = this;

                return {
                    chart: {
                        // events: {
                        //     render: function() {
                        //         //Make sure the chart has loaded
                        //         if(this.legend.offsetWidth == 0)
                        //         {
                        //             return;
                        //         }

                        //         //Check if we need to remove an old level
                        //         if(this.helperLabel)
                        //         {
                        //             this.helperLabel.destroy();
                        //         }

                        //         //Build it
                        //         var text = '<i class="fa fa-question-circle legend-helper" title="Click on a particular legend to Hide the corresponding line graph"></i>';
                        //         this.helperLabel = this.renderer.label(text, null, null, null, null, null, true)
                        //                         .css({
                        //                             width: '100px',
                        //                             color: '#222',
                        //                             fontSize: '16px'
                        //                         })
                        //                         .attr({
                        //                             'stroke': 'silver',
                        //                             'stroke-width': 1,
                        //                             'r': 2,
                        //                             'padding': 3.5
                        //                         })
                        //                         .add();

                        //         //Debugging
                        //         var xOffset = (-1 * this.legend.legendWidth) - 20;

                        //         //Align it
                        //         this.helperLabel.align(Highcharts.extend(this.helperLabel.getBBox(), {
                        //             align: 'right',
                        //             x: xOffset, // offset
                        //             verticalAlign: 'bottom',
                        //             y: 0 // offset
                        //         }), null, 'spacingBox');
                        //     }
                        // },
                        type: 'line',
                    },
                    title: {
                        text: 'Inspection, Observations, Corrective Actions'
                    },
                    xAxis: {
                        categories: this.labels
                    },
                    yAxis: {
                        title: {
                            text: '# of Entries'
                        },
                        min: 0,
                        //max: 110,
                        //tickInterval: 10,
                        //tickPixelInterval: 20
                    },
                    plotOptions: {
                        bar: {
                            dataLabels: {
                                enabled: true,
                                formatter: function(){
                                    return this.point.series.name+': '+this.point.y;
                                }
                            }
                        }
                    },
                    legend: {
                        layout: 'horizontal',
                        align: 'right',
                        verticalAlign: 'bottom',
                        x: 0,
                        y: 0,
                        floating: false,
                        borderColor: '#dddddd',
                        borderWidth: 1,
                    },
                    series: this.line_chart_series,
                    tooltip: {
                        formatter: function() {
                            //Send it
                            var html = 'Type: <b>'+ this.series.name + '</b><br/>' +
                                    'Date: <b>'+ this.x + '</b><br/>' +
                                    'Total: <b>'+this.point.total+'</b>';

                            return html;
                        }
                    },
                }
            },

            //Major Body Part Options
            reportTypeOptions(){
                //Setup options
                var options = [
                    {value: null, text: '-- Show All Data --'},
                    {value: 'Division', text: 'Division'},
                    {value: 'Employee', text: 'Employee'},
                    {value: 'Jobsite', text: 'Jobsite'},
                    {value: 'User', text: 'User'},
                ];

                //Send it
                return options;
            },
        },

        watch: {
            'filters.report_type'(newVal, oldVal){

            },

            // pdf_view(newVal, oldVal){
            //     //Set the location
            //     window.location = '/reports/totals-charts' + (newVal === true ? '?pdf=true' : '');
            // },

            pdf_view(newVal, oldVal){
                //Helper?
                this.loading = true;

                //Redraw the Draw
                this.getData();
            },
        },

        methods: {

            clearFilters() {
                //Reset each

                this.filters.interval = [{'name': 'Monthly', 'id': 'Month'}];
                this.filters.inspection_forms = [];
                this.filters.date_range = 'ytd';
                this.filters.report_type = null;

                //REset Type Values
                this.filters.JobsiteId = null;
                this.filters.DivisionId = null;
                this.filters.UserId = null;
                this.filters.EmployeeId = null;
                this.type_label = null;

                //Rerend
                this.$refs.inspDate.setValue('ytd');
                this.$refs.inspDate.updateFilter();
            },

            applyFilters() {
                //Send it
                //this.$refs.partTable.getData();
                this.$emit('filtersChanged', this.filters);

                this.getData();

                //Close it
                this.closeFilters(); //Update the state
            },

            closeFilters() {
                //Hide the modal
                this.show_avg_filters = false;
            },

            setReportTypeValue(data){
                //Check for Nul
                if(!data || (Array.isArray(data) && !data[0]))
                {
                    //Reset all the values
                    this.filters.JobsiteId = null;
                    this.filters.DivisionId = null;
                    this.filters.UserId = null;
                    this.filters.EmployeeId = null;
                    this.type_label = null;

                    //Stop here
                    return;
                }

                //Set the Type
                if(data.JobsiteId)
                {
                    //Set the ID
                    this.filters.JobsiteId = data.JobsiteId;

                    //Set the filter
                    this.type_label = data.JobsiteNumber + ' - ' + data.JobsiteName;
                }
                else if(data.UserId)
                {
                    //Set the ID
                    this.filters.UserId = data.UserId;

                    //Set the Label
                    this.type_label = data.FirstName + ' ' + data.LastName;
                }
                else if(data.EmployeeId)
                {
                    //Set the ID
                    this.filters.EmployeeId = data.EmployeeId;

                    //Set the Label
                    this.type_label = data.FirstName + ' ' + data.LastName + ' - ' + data.EmployeeNumber;
                }
                else if(Array.isArray(data))
                {
                    //Set the ID
                    this.filters.DivisionId = data[0].id;

                    //Set the Label
                    this.type_label = data[0].name;
                }
            },

            getData() {
                //Loading?
                this.loading = true;

                //SEtup
                var filters = JSON.parse(JSON.stringify(this.filters));

                //Map Some Stuff
                filters.interval = this.filters.interval[0].id;
                filters.inspection_forms = this.filters.inspection_forms.map(e => e.id);

                //Send it
                this.$http
                    .get('reports/totals-data', filters)
                    .then(response => {
                        //Set the Seris
                        this.compliantNoncompliantSeries = response.data.compliantNoncompliantSeries;

                        //Set the data
                        this.total_insps = response.data.total_insps;
                        this.total_obsvs = response.data.total_obsvs;
                        this.total_actions = response.data.total_actions;
                        this.total_compliant = response.data.total_compliant;
                        this.total_noncompliant = response.data.total_noncompliant;
                        this.avg_ttc = response.data.avg_ttc;
                        this.labels = response.data.labels;
                        this.line_chart_series = response.data.line_chart_series;

                        //User Counts
                        this.user_actions_created = response.data.user_actions_created;
                        this.user_actions_completed = response.data.user_actions_completed;
                        this.user_actions_assigned = response.data.user_actions_assigned;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            getFilters() {
                //Loading?

                //Send the response
                this.$http
                    .get('reports/filters/avg-score', this.filters)
                    .then(response => {
                        //Set all the data
                        this.divisions = response.data.divisions;
                        this.inspection_forms = response.data.inspection_forms;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },
        },

        mounted() {
            //this.getData()
            this.getFilters();
        }
    }
</script>

<style type="text/css" scoped>
    .legend-helper {
        position: absolute;
        left: -20px;
    }
</style>